import { useEffect, useState } from 'react'
import { OrbitControls as OrbitControlsImpl } from 'three-stdlib'

function ZoomButtons() {
  const [controls, setControls] = useState<OrbitControlsImpl | null>(null)
  const [currentDistance, setCurrentDistance] = useState<number | null>(null)
  const MIN_DISTANCE = 10
  const MAX_DISTANCE = 35


  useEffect(() => {
    // Check for controls periodically until they're available
    const interval = setInterval(() => {
      const ctrl = (window as any).__orbit_controls__
      if (ctrl) {
        setControls(ctrl)
        // Get initial distance
        setCurrentDistance(ctrl.getDistance())
        clearInterval(interval)
      }
    }, 100)

    return () => clearInterval(interval)
  }, [])

  // Track distance changes
  useEffect(() => {
    if (!controls) return

    const updateDistance = () => {
      setCurrentDistance(controls.getDistance())
    }

    // Listen to controls change event
    controls.addEventListener('change', updateDistance)

    // Also check periodically during animation
    const interval = setInterval(updateDistance, 50)

    return () => {
      controls.removeEventListener('change', updateDistance)
      clearInterval(interval)
    }
  }, [controls])

  const isZoomInDisabled = currentDistance !== null && currentDistance <= MIN_DISTANCE
  const isZoomOutDisabled = currentDistance !== null && currentDistance >= MAX_DISTANCE

  const animateZoom = (targetScale: number, duration: number = 300) => {
    if (!controls) return
    
    const startScale = controls.object.position.length()
    const endScale = Math.max(MIN_DISTANCE, Math.min(MAX_DISTANCE, startScale * targetScale))
    const startTime = performance.now()
    
    const animate = () => {
      const elapsed = performance.now() - startTime
      const progress = Math.min(elapsed / duration, 1)
      
      // Easing function for smooth animation
      const easeOut = 1 - Math.pow(1 - progress, 3)
      
      const currentScale = startScale + (endScale - startScale) * easeOut
      const direction = controls.object.position.clone().normalize()
      controls.object.position.copy(direction.multiplyScalar(currentScale))
      controls.update()
      
      // Update distance state during animation
      setCurrentDistance(controls.getDistance())
      
      if (progress < 1) {
        requestAnimationFrame(animate)
      }
    }
    
    animate()
  }

  const handleZoomIn = () => {
    if (controls && !isZoomInDisabled) {
      // Zoom in by moving camera closer (scale < 1)
      animateZoom(0.8, 300)
    }
  }

  const handleZoomOut = () => {
    if (controls && !isZoomOutDisabled) {
      // Zoom out by moving camera further (scale > 1)
      animateZoom(1.25, 300)
    }
  }

  return (
    <div className="absolute top-4 right-4 z-10">
      <div className="bg-white rounded-full border border-gray-300 overflow-hidden flex flex-col">
        {/* Zoom In Button - Top button with plus icon */}
        <button
          onClick={handleZoomIn}
          disabled={isZoomInDisabled}
          className={`p-3 transition-colors flex items-center justify-center border-b border-gray-300 ${
            isZoomInDisabled 
              ? 'opacity-40 cursor-not-allowed' 
              : 'hover:bg-gray-50 cursor-pointer'
          }`}
          aria-label="Zoom in"
        >
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none">
    <path
      fill="#666"
      d="M9.5 8H8v1.5a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5V8H4.5a.5.5 0 0 1-.5-.5v-1a.5.5 0 0 1 .5-.5H6V4.5a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5V6h1.5a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5"
    ></path>
    <path
      fill="#666"
      d="M7 14a7 7 0 1 1 7-7 7.01 7.01 0 0 1-7 7M7 2a5 5 0 1 0 5 5 5.006 5.006 0 0 0-5-5m8.707 12.293L13.314 11.9a8 8 0 0 1-1.414 1.414l2.393 2.393a1 1 0 0 0 1.414-1.414"
    ></path>
  </svg>
        </button>
        
        {/* Zoom Out Button - Bottom button with minus icon */}
        <button
          onClick={handleZoomOut}
          disabled={isZoomOutDisabled}
          className={`p-3 transition-colors flex items-center justify-center ${
            isZoomOutDisabled 
              ? 'opacity-40 cursor-not-allowed' 
              : 'hover:bg-gray-50 cursor-pointer'
          }`}
          aria-label="Zoom out"
        >
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none">
    <g fill="#666" clipPath="url(#a)">
      <path d="M4.5 6h5a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-5a.5.5 0 0 1-.5-.5v-1a.5.5 0 0 1 .5-.5"></path>
      <path d="M7 14a7 7 0 1 1 7-7 7.01 7.01 0 0 1-7 7M7 2a5 5 0 1 0 5 5 5.006 5.006 0 0 0-5-5m8.707 12.293L13.314 11.9a8 8 0 0 1-1.414 1.414l2.393 2.393a1 1 0 0 0 1.414-1.414"></path>
    </g>
    <defs>
      <clipPath id="a">
        <path fill="#fff" d="M0 0h16v16H0z"></path>
      </clipPath>
    </defs>
  </svg>
        </button>
      </div>
    </div>
  )
}

export default ZoomButtons

